import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import "../styles.css";

export default function Tools() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/products.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        const formattedProducts = data.map(p => {
          const tag = p.buttonText && p.buttonText.startsWith('$') ? 'premium' : (p.cost || 'free').toLowerCase();
          return {
            id: p.id, // Added for keys and general use
            img: p.overlayImage,
            alt: p.name,
            tag: tag,
            title: p.name,
            description: p.description,
            link: p.buttonLink,
            btnClass: p.cost === 'Premium' ? 'btn btn-product-premium2' : 'btn btn-product',
            btnText: p.buttonText, // Use dynamic button text
            categories: [p.cost, ...(p.tags || []), p.location].filter(Boolean).concat(tag === 'premium' ? 'Premium' : []).concat(tag === 'free' ? 'Free' : []),
            location: p.location, // Added for specific "Any" location check
          };
        });

        setProducts(formattedProducts.reverse());
      } catch (e) {
        console.error("Failed to fetch products:", e);
        setError("Failed to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const staticCategories = [
    "All Products",
    "Premium",
    "Free",
    "Streamer.bot",
    "YouTube",
    "Twitch",
    "Emotes",
    "Badges"
  ];
  const [selectedCategory, setSelectedCategory] = useState("All Products");

  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || product.title.toLowerCase().includes(searchTerm.toLowerCase());

    if (selectedCategory === "All Products") {
      return matchesSearch;
    }

    const hasCategory = product.categories && product.categories.includes(selectedCategory);

    if (["Twitch", "YouTube"].includes(selectedCategory) && product.location === "Any") {
      return matchesSearch;
    }

    return hasCategory && matchesSearch;
  });

  const renderContent = () => {
    if (loading) {
      return (
        <div className="shop-loading">
          <div className="spinner"></div>
          <p>Loading shop...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="shop-error">
          <p>{error}</p>
        </div>
      );
    }

    return (
      <>
        <div className="shop-introduction">
          <p>Browse my curated collection of tools and resources for streamers. Whether you're just starting or looking to upgrade your setup, you'll find something useful here!</p>
        </div>
        <div className="games-search">
          <div className="search-filter-row">
            <div className="search-container">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search tools..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button
                  className="search-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            <div className="category-dropdown-wrapper">
              <select
                className="category-dropdown"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {staticCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <i className="fas fa-chevron-down category-dropdown-arrow"></i>
            </div>
          </div>
        </div>
        <div className="product-grid">
          {filteredProducts.map((product, idx) => (
            <div className="product-card" key={product.id || idx}>
              <div className="product-image">
                <img src={product.img} alt={product.alt} />
              </div>
              <div className="product-info">
                <div className={`product-tag ${product.tag === 'premium' ? 'premium2' : product.tag}`}>{product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}</div>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                {product.link.startsWith('/') ? (
                                <Link to={product.link} className={product.btnClass}>{product.btnText}</Link>
                              ) : (
                                <a href={product.link} className={product.btnClass} target="_blank" rel="noopener noreferrer">{product.btnText}</a>
                              )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Stream Tools & Overlays"
        description="Download free and premium streaming tools, OBS overlays, Streamer.bot extensions, alerts, and widgets. Enhance your Twitch or YouTube stream with ZeMaxi's custom tools."
        keywords="streaming tools, OBS overlays, Streamer.bot extensions, free stream tools, Twitch overlays, stream alerts, stream widgets, content creator tools"
        url="https://zemaxi.com/tools"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="shop-hero noise-bg">
          <div className="container">
            <h1>Stream <span className="highlight">Tools</span></h1>
            <p>Discover free and premium tools to enhance your stream!</p>
          </div>
        </section>
        <section className="shop-section">
          <div className="container shop-container">
            {renderContent()}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
